import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useSettings } from "@/lib/settings";
import { Save, Plus, Trash2 } from "lucide-react";

const cls = "w-full mt-1 px-3 py-2 border border-neutral-200 rounded-lg text-sm";

function Field({ label, value, onChange, type = "text", rows, testId }) {
  if (type === "checkbox") {
    return (
      <label className="flex items-center gap-2">
        <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} data-testid={testId} />
        <span className="text-sm font-semibold text-neutral-700">{label}</span>
      </label>
    );
  }
  return (
    <div>
      <label className="text-xs font-semibold text-neutral-600">{label}</label>
      {type === "textarea"
        ? <textarea rows={rows || 3} value={value || ""} onChange={(e) => onChange(e.target.value)} className={cls} data-testid={testId} />
        : <input value={value || ""} onChange={(e) => onChange(e.target.value)} className={cls} data-testid={testId} />}
    </div>
  );
}

const TABS = [
  { key: "hero", label: "Hero & Banner" },
  { key: "video", label: "Video" },
  { key: "features", label: "Features" },
  { key: "benefits", label: "Benefits" },
  { key: "testimonials", label: "Testimonials" },
  { key: "cta", label: "CTA & Files" },
  { key: "seo", label: "SEO" },
];

export default function AdminPartner() {
  const { refresh } = useSettings();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("hero");

  useEffect(() => { api.get("/settings").then(({ data }) => setData(data)); }, []);
  if (!data) return <div className="p-8">Loading...</div>;

  const p = data.partner_page || {};
  const setP = (key, value) => setData({ ...data, partner_page: { ...p, [key]: value } });
  const setPSeo = (key, value) => setData({ ...data, partner_page: { ...p, seo: { ...(p.seo || {}), [key]: value } } });

  const setListItem = (listKey, i, field, val) => setP(listKey, (p[listKey] || []).map((x, idx) => idx === i ? { ...x, [field]: val } : x));
  const addListItem = (listKey, template) => setP(listKey, [...(p[listKey] || []), template]);
  const removeListItem = (listKey, i) => setP(listKey, (p[listKey] || []).filter((_, idx) => idx !== i));

  const save = async () => {
    try { await api.put("/settings", { data }); toast.success("Saved"); refresh(); }
    catch { toast.error("Save failed"); }
  };

  return (
    <div className="p-8 pb-24 max-w-5xl">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <h1 className="font-display font-black text-3xl" data-testid="admin-partner-title">Partner With Us — Page Content</h1>
        <button onClick={save} className="btn-primary-eq text-sm" data-testid="partner-save"><Save className="w-4 h-4" /> Save</button>
      </div>

      <div className="flex gap-2 mb-6 flex-wrap">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${tab === t.key ? "bg-neutral-900 text-white" : "bg-white border border-neutral-200 text-neutral-700 hover:bg-neutral-100"}`}
            data-testid={`partner-tab-${t.key}`}>{t.label}</button>
        ))}
      </div>

      <div className="bg-white rounded-2xl p-6 border border-neutral-200 grid gap-4">
        {tab === "hero" && (<>
          <Field label="Show this page (enabled)" type="checkbox" value={p.enabled !== false} onChange={(v) => setP("enabled", v)} testId="partner-enabled" />
          <Field label="Kicker (small label)" value={p.hero_kicker} onChange={(v) => setP("hero_kicker", v)} testId="partner-hero_kicker" />
          <Field label="Hero title" type="textarea" rows={2} value={p.hero_title} onChange={(v) => setP("hero_title", v)} testId="partner-hero_title" />
          <Field label="Hero subtitle / SEO content" type="textarea" rows={6} value={p.hero_subtitle} onChange={(v) => setP("hero_subtitle", v)} testId="partner-hero_subtitle" />
          <Field label="Hero banner image URL" value={p.hero_image} onChange={(v) => setP("hero_image", v)} testId="partner-hero_image" />
        </>)}

        {tab === "video" && (<>
          <Field label="Video heading" value={p.video_heading} onChange={(v) => setP("video_heading", v)} testId="partner-video_heading" />
          <Field label="YouTube video URL (watch/share/embed link)" value={p.video_url} onChange={(v) => setP("video_url", v)} testId="partner-video_url" />
          <p className="text-xs text-neutral-500">Paste any YouTube link — it's auto-converted to an embed. Leave blank to hide the video section.</p>
        </>)}

        {tab === "features" && (<>
          <Field label="Section heading" value={p.features_heading} onChange={(v) => setP("features_heading", v)} testId="partner-features_heading" />
          {(p.features || []).map((f, i) => (
            <div key={i} className="p-4 rounded-xl border border-neutral-200 bg-neutral-50 grid gap-2" data-testid={`partner-feature-row-${i}`}>
              <div className="flex justify-between items-center"><span className="text-xs font-semibold text-neutral-500">Feature {i + 1}</span>
                <button onClick={() => removeListItem("features", i)} className="text-red-600 text-xs flex items-center gap-1"><Trash2 className="w-3 h-3" /> Remove</button></div>
              <input placeholder="Icon (Store, Wallet, Rocket, ShieldCheck, TrendingUp, Award, Users, HeartHandshake)" value={f.icon || ""} onChange={(e) => setListItem("features", i, "icon", e.target.value)} className={cls} />
              <input placeholder="Title" value={f.title || ""} onChange={(e) => setListItem("features", i, "title", e.target.value)} className={cls} />
              <textarea rows={2} placeholder="Description" value={f.desc || ""} onChange={(e) => setListItem("features", i, "desc", e.target.value)} className={cls} />
            </div>
          ))}
          <button onClick={() => addListItem("features", { icon: "Store", title: "", desc: "" })} className="btn-outline-eq text-sm w-fit"><Plus className="w-4 h-4" /> Add feature</button>
        </>)}

        {tab === "benefits" && (<>
          <Field label="Section heading" value={p.benefits_heading} onChange={(v) => setP("benefits_heading", v)} testId="partner-benefits_heading" />
          {(p.benefits || []).map((b, i) => (
            <div key={i} className="p-4 rounded-xl border border-neutral-200 bg-neutral-50 grid gap-2" data-testid={`partner-benefit-row-${i}`}>
              <div className="flex justify-between items-center"><span className="text-xs font-semibold text-neutral-500">Benefit {i + 1}</span>
                <button onClick={() => removeListItem("benefits", i)} className="text-red-600 text-xs flex items-center gap-1"><Trash2 className="w-3 h-3" /> Remove</button></div>
              <input placeholder="Title" value={b.title || ""} onChange={(e) => setListItem("benefits", i, "title", e.target.value)} className={cls} />
              <textarea rows={2} placeholder="Description" value={b.desc || ""} onChange={(e) => setListItem("benefits", i, "desc", e.target.value)} className={cls} />
            </div>
          ))}
          <button onClick={() => addListItem("benefits", { title: "", desc: "" })} className="btn-outline-eq text-sm w-fit"><Plus className="w-4 h-4" /> Add benefit</button>
        </>)}

        {tab === "testimonials" && (<>
          <Field label="Section heading" value={p.testimonials_heading} onChange={(v) => setP("testimonials_heading", v)} testId="partner-testimonials_heading" />
          {(p.testimonials || []).map((t, i) => (
            <div key={i} className="p-4 rounded-xl border border-neutral-200 bg-neutral-50 grid gap-2" data-testid={`partner-testimonial-row-${i}`}>
              <div className="flex justify-between items-center"><span className="text-xs font-semibold text-neutral-500">Testimonial {i + 1}</span>
                <button onClick={() => removeListItem("testimonials", i)} className="text-red-600 text-xs flex items-center gap-1"><Trash2 className="w-3 h-3" /> Remove</button></div>
              <input placeholder="Name" value={t.name || ""} onChange={(e) => setListItem("testimonials", i, "name", e.target.value)} className={cls} />
              <input placeholder="Role" value={t.role || ""} onChange={(e) => setListItem("testimonials", i, "role", e.target.value)} className={cls} />
              <input placeholder="Photo URL" value={t.image || ""} onChange={(e) => setListItem("testimonials", i, "image", e.target.value)} className={cls} />
              <textarea rows={2} placeholder="Quote" value={t.quote || ""} onChange={(e) => setListItem("testimonials", i, "quote", e.target.value)} className={cls} />
            </div>
          ))}
          <button onClick={() => addListItem("testimonials", { name: "", role: "", image: "", quote: "" })} className="btn-outline-eq text-sm w-fit"><Plus className="w-4 h-4" /> Add testimonial</button>
        </>)}

        {tab === "cta" && (<>
          <Field label="Primary CTA button text" value={p.cta_primary_text} onChange={(v) => setP("cta_primary_text", v)} testId="partner-cta_primary_text" />
          <Field label="Secondary CTA button text (brochure)" value={p.cta_secondary_text} onChange={(v) => setP("cta_secondary_text", v)} testId="partner-cta_secondary_text" />
          <Field label="Brochure PDF URL" value={p.brochure_url} onChange={(v) => setP("brochure_url", v)} testId="partner-brochure_url" />
          <Field label="Partnership agreement URL" value={p.agreement_url} onChange={(v) => setP("agreement_url", v)} testId="partner-agreement_url" />
          <Field label="Bottom CTA heading" value={p.cta_heading} onChange={(v) => setP("cta_heading", v)} testId="partner-cta_heading" />
          <Field label="Bottom CTA subtitle" type="textarea" rows={2} value={p.cta_subtitle} onChange={(v) => setP("cta_subtitle", v)} testId="partner-cta_subtitle" />
        </>)}

        {tab === "seo" && (<>
          <Field label="Meta title" value={p.seo?.meta_title} onChange={(v) => setPSeo("meta_title", v)} testId="partner-meta_title" />
          <Field label="Meta description" type="textarea" rows={2} value={p.seo?.meta_description} onChange={(v) => setPSeo("meta_description", v)} testId="partner-meta_description" />
          <Field label="Keywords" value={p.seo?.keywords} onChange={(v) => setPSeo("keywords", v)} testId="partner-keywords" />
        </>)}
      </div>
    </div>
  );
}
